import { FC, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  checkSignatories,
  isSignatoryLookupAborted,
  SignatoryCheck,
} from '../../services/service-signatory';
import SignatoryPanel from './SignatoryPanel';

type Props = {
  organizationNumber?: string;
  signeeNames: string[];
  isOpen?: boolean;
};

const isLookupableOrgNumber = (value?: string): value is string =>
  !!value && /^\d{6}-?\d{4}$/.test(value.trim());

const SignatoryInfo: FC<Props> = ({ organizationNumber, signeeNames, isOpen = true }) => {
  const [check, setCheck] = useState<SignatoryCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const signeeKey = signeeNames.join('|');

  useEffect(() => {
    if (!isLookupableOrgNumber(organizationNumber)) {
      setCheck(null);
      setFailed(false);
      return;
    }
    if (!isOpen) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const result = await checkSignatories(organizationNumber, signeeNames, controller.signal);
        setCheck(result);
      } catch (error) {
        if (isSignatoryLookupAborted(error)) return;
        setCheck(null);
        setFailed(true);
        Sentry.captureException(error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [organizationNumber, signeeKey, isOpen]);

  return <SignatoryPanel check={check} loading={loading} failed={failed} />;
};

export default SignatoryInfo;
